import {
  AlertTriangle,
  ArrowRight,
  BookOpenText,
  Check,
  ChevronLeft,
  FileDiff,
  GitBranch,
  ListTree,
  LoaderCircle,
  MessageSquareText,
  RotateCcw,
  Send,
  X
} from "lucide-react";
import { type FormEvent, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { api, postJson } from "../api";
import { useDialogLifecycle } from "../dialog";
import { confirmDiscard, useUnsavedChanges } from "../interaction";
import { choiceKeyDown } from "../keyboard";
import type { BookRevisionProjectPatch, BookRevisionScope, BookRevisionSession, Project } from "../types";
import { StatusDot, relativeTime } from "./Ui";

interface BookRevisionStudioProps {
  open: boolean;
  project: Project;
  onClose: () => void;
  onApplied: (documentId?: string) => Promise<void>;
  notify: (message: string, tone?: "success" | "error" | "info") => void;
}

const scopeOptions: Array<{
  id: BookRevisionScope;
  label: string;
  detail: string;
  icon: typeof BookOpenText;
}> = [
  { id: "story", label: "整本故事", detail: "主线、人物、世界、结局与整体承诺", icon: BookOpenText },
  { id: "ledger", label: "设定账本", detail: "人物、关系、伏笔、时间线与规则", icon: GitBranch },
  { id: "outline", label: "分卷章纲", detail: "分卷结构、阶段事件与后续节奏", icon: ListTree }
];

export function BookRevisionStudio({ open, project, onClose, onApplied, notify }: BookRevisionStudioProps) {
  const [revision, setRevision] = useState<BookRevisionSession | null>(null);
  const [recent, setRecent] = useState<BookRevisionSession[]>([]);
  const [scope, setScope] = useState<BookRevisionScope>("story");
  const [brief, setBrief] = useState("");
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState(false);
  const [applying, setApplying] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const chatEnd = useRef<HTMLDivElement | null>(null);
  const requestLock = useRef(false);
  const [historyError, setHistoryError] = useState("");
  const [historyReload, setHistoryReload] = useState(0);
  const [syncError, setSyncError] = useState("");
  const [uncertain, setUncertain] = useState(false);
  const dialogRef = useDialogLifecycle(open, onClose, busy || applying);
  useUnsavedChanges(Boolean(brief.trim() || message.trim()), "整书修订目标或未发送消息", busy || applying);

  useEffect(() => {
    setRevision(null);
    setRecent([]);
    setBrief("");
    setMessage("");
    setConfirming(false);
  }, [project.id]);

  useEffect(() => {
    if (!open) return;
    const controller = new AbortController(); setHistoryError("");
    api<{ revisions: BookRevisionSession[] }>(`/api/projects/${project.id}/book-revisions`, { signal: controller.signal })
      .then((result) => { if (!controller.signal.aborted) setRecent(result.revisions); })
      .catch((error) => { if (!controller.signal.aborted) setHistoryError(error instanceof Error ? error.message : "修订记录加载失败"); });
    return () => controller.abort();
  }, [open, project.id, historyReload]);

  useEffect(() => {
    if (!open || revision?.status !== "responding") return;
    let cancelled = false; let inFlight = false;
    const refresh = async () => {
      if (inFlight || requestLock.current || cancelled) return;
      inFlight = true;
      try {
        const latest = await api<BookRevisionSession>(`/api/projects/${project.id}/book-revisions/${revision.id}`);
        if (cancelled) return;
        setRevision(latest); setSyncError("");
        setRecent((current) => [latest, ...current.filter((item) => item.id !== latest.id)]);
      } catch { if (!cancelled) setSyncError("进度连接暂时中断，正在重新获取；请勿重复发送当前消息。"); }
      finally { inFlight = false; }
    };
    const timer = window.setInterval(() => { void refresh(); }, 3000);
    return () => {
      cancelled = true;
      window.clearInterval(timer);
    };
  }, [open, project.id, revision?.id, revision?.status]);

  useEffect(() => {
    chatEnd.current?.scrollIntoView({ behavior: "smooth" });
  }, [revision?.messages.length, busy]);

  const projectPatchEntries = useMemo(() => revision ? projectPatchRows(revision.proposal.projectPatch) : [], [revision?.proposal.projectPatch]);
  const hasProposal = Boolean(revision && (revision.proposal.changes.length || projectPatchEntries.length));
  const agentWorking = busy || revision?.status === "responding";
  if (!open) return null;

  async function startRevision(event: FormEvent) {
    event.preventDefault();
    if (requestLock.current || brief.trim().length < 5) return;
    requestLock.current = true;
    setBusy(true);
    try {
      const next = await postJson<BookRevisionSession>(`/api/projects/${project.id}/book-revisions`, { scope, brief: brief.trim() });
      setRevision(next);
      setRecent((current) => [next, ...current.filter((item) => item.id !== next.id)]);
      setBrief("");
    } catch (error) {
      notify(error instanceof Error ? error.message : "整书修订讨论启动失败", "error");
    } finally {
      requestLock.current = false;
      setBusy(false);
    }
  }

  async function openRevision(item: BookRevisionSession) {
    if (requestLock.current) return;
    const same = revision?.id === item.id;
    if (!same && !await confirmDiscard(Boolean(brief.trim() || message.trim()), "未发送的修订输入")) return;
    requestLock.current = true;
    setBusy(true);
    try {
      const latest = await api<BookRevisionSession>(`/api/projects/${project.id}/book-revisions/${item.id}`);
      if (!same || (uncertain && latest.messages.slice(revision?.messages.length ?? 0).some((turn) => turn.role === "user" && turn.content === message.trim()))) setMessage("");
      setRevision(latest); setBrief(""); setSyncError(""); setUncertain(false);
      setConfirming(false);
    } catch (error) {
      notify(error instanceof Error ? error.message : "修订讨论加载失败", "error");
    } finally {
      requestLock.current = false;
      setBusy(false);
    }
  }

  async function sendMessage(event: FormEvent) {
    event.preventDefault();
    if (!revision || !message.trim() || requestLock.current || uncertain || revision.status === "applied" || revision.status === "responding") return;
    requestLock.current = true;
    const text = message.trim();
    const previous = revision;
    setMessage("");
    setConfirming(false);
    setBusy(true);
    setRevision({
      ...revision,
      status: "responding",
      messages: [...revision.messages, {
        id: `local-${Date.now()}`,
        role: "user",
        content: text,
        createdAt: new Date().toISOString()
      }]
    });
    try {
      const next = await postJson<BookRevisionSession>(`/api/projects/${project.id}/book-revisions/${revision.id}/messages`, { message: text });
      setRevision(next);
      setRecent((current) => [next, ...current.filter((item) => item.id !== next.id)]);
    } catch (error) {
      try {
        const latest = await api<BookRevisionSession>(`/api/projects/${project.id}/book-revisions/${previous.id}`);
        setRevision(latest);
        setRecent((current) => [latest, ...current.filter((item) => item.id !== latest.id)]);
        if (!latest.messages.slice(previous.messages.length).some((item) => item.role === "user" && item.content === text)) setMessage(text);
      } catch {
        setRevision(previous);
        setMessage(text); setUncertain(true); setSyncError("暂时无法确认消息是否已经收到。输入仍保留，请先刷新讨论状态再决定是否重发。");
      }
      notify(error instanceof Error ? error.message : "消息发送失败", "error");
    } finally {
      requestLock.current = false;
      setBusy(false);
    }
  }

  async function applyRevision() {
    if (!revision || revision.status !== "ready" || requestLock.current || !confirming) return;
    if (message.trim()) { notify("还有未发送的补充意见，请先发送或清除输入，再应用修订", "info"); return; }
    requestLock.current = true;
    setApplying(true);
    try {
      const next = await postJson<BookRevisionSession>(`/api/projects/${project.id}/book-revisions/${revision.id}/apply`);
      setRevision(next);
      setRecent((current) => [next, ...current.filter((item) => item.id !== next.id)]);
      setConfirming(false);
      await onApplied(next.proposal.changes[0]?.documentId);
      if (next.error) notify(next.error, "error");
      else notify(next.proposal.replanFromChapter !== null ? "正典已写入，首批新章卡已开始生成" : "整书修订已写入故事圣经", "success");
    } catch (error) {
      notify(error instanceof Error ? error.message : "修订应用失败", "error");
    } finally {
      requestLock.current = false;
      setApplying(false);
    }
  }

  async function reset() {
    if (requestLock.current || !await confirmDiscard(Boolean(brief.trim() || message.trim()), "未发送的修订输入")) return;
    setHistoryReload((value) => value + 1); setSyncError(""); setUncertain(false);
    setRevision(null);
    setBrief("");
    setMessage("");
    setScope("story");
    setConfirming(false);
  }

  return createPortal(
    <div ref={(node) => { dialogRef.current = node; }} tabIndex={-1} className="book-revision-overlay" aria-busy={busy || applying} role="dialog" aria-modal="true" aria-label="整书讨论与修订">
      <header className="book-revision-header">
        <div className="book-revision-brand"><span><MessageSquareText size={18} /></span><div><strong>整书讨论与修订</strong><small>《{project.title}》· 框架 Agent</small></div></div>
        <div className="book-revision-header-state">{revision && <><StatusDot status={revision.status === "applied" ? "completed" : revision.status === "responding" ? "running" : "warning"} /><span>{revisionStatusLabel(revision.status)}</span></>}</div>
        <button className="icon-button" type="button" disabled={busy || applying} onClick={onClose} title="关闭，修订讨论保留" aria-label="关闭整书修订"><X size={18} /></button>
      </header>

      {!revision ? <main className="book-revision-start">
        <section className="book-revision-intro">
          <span className="start-icon"><BookOpenText size={24} /></span>
          <div><span className="section-kicker">BOOK REVISION</span><h1>重新讨论这本书</h1><p>选择修订范围。框架 Agent 将基于当前正典与定稿事实形成草案。</p></div>
        </section>
        <form className="book-revision-start-form" onSubmit={startRevision}>
          <fieldset><legend>修订范围</legend><div className="book-revision-scope" role="radiogroup" aria-label="修订范围" onKeyDown={choiceKeyDown}>{scopeOptions.map((option) => { const Icon = option.icon; return <button key={option.id} type="button" disabled={busy} role="radio" aria-checked={scope === option.id} tabIndex={scope === option.id ? 0 : -1} className={scope === option.id ? "active" : ""} onClick={() => setScope(option.id)}><Icon size={18} /><span><strong>{option.label}</strong><small>{option.detail}</small></span></button>; })}</div></fieldset>
          <label htmlFor="book-revision-brief"><span>这次想修正什么？</span><textarea data-autofocus disabled={busy} minLength={5} maxLength={20000} id="book-revision-brief" onKeyDown={(event) => { if ((event.ctrlKey || event.metaKey) && event.key === "Enter" && !event.nativeEvent.isComposing) { event.preventDefault(); event.currentTarget.form?.requestSubmit(); } }} value={brief} onChange={(event) => setBrief(event.target.value)} placeholder="例如：重新讨论主角和导师的关系。导师不能过早知道核心秘密，同时检查人物账本、关系网和第一卷事件是否需要一起调整。" autoFocus /></label>
          <p className="ux-live-status" role="status">{brief.trim().length < 5 ? `至少描述 5 字，还需 ${5 - brief.trim().length} 字。` : "Ctrl+Enter / ⌘Enter 开始讨论。草案应用前不会修改正典。"}</p>
          <button className="button button-primary" type="submit" disabled={busy || brief.trim().length < 5}>{busy ? <LoaderCircle size={16} className="spin-icon" /> : <MessageSquareText size={16} />}{busy ? "框架 Agent 正在读取正典" : "开始讨论"}</button>
        </form>
        {historyError && <div className="ux-error" role="alert"><p>{historyError}</p><button className="button button-secondary" type="button" disabled={busy} onClick={() => setHistoryReload((value) => value + 1)}>重试加载修订记录</button></div>}
        {recent.length > 0 && <section className="book-revision-history"><header><h2>修订记录</h2><span>{recent.length}</span></header><div>{recent.map((item) => <button type="button" key={item.id} onClick={() => void openRevision(item)} disabled={busy}><StatusDot status={item.status === "applied" ? "completed" : item.status === "responding" ? "running" : "warning"} /><span><strong>{item.title}</strong><small>{revisionScopeLabel(item.scope)} · {revisionStatusLabel(item.status)} · {relativeTime(item.updatedAt)}</small></span><ArrowRight size={15} /></button>)}</div></section>}
      </main> : <main className="book-revision-workspace">
        <section className="book-revision-chat">
          <header><button type="button" className="icon-button" title="返回修订记录" disabled={busy || applying} onClick={() => void reset()}><ChevronLeft size={17} /></button><div><strong>{revision.title}</strong><small>{revisionScopeLabel(revision.scope)} · {revision.model}</small></div></header>
          <div className="book-revision-messages">{revision.messages.map((item) => <article key={item.id} className={`message-${item.role}`}><span>{item.role === "user" ? "作者" : "框"}</span><div>{item.content}</div></article>)}{agentWorking && <article className="message-assistant thinking"><span>框</span><div><LoaderCircle size={16} className="spin-icon" />正在核对正典和连带影响</div></article>}<div ref={chatEnd} /></div>
          {syncError && <p className="ux-error" role="alert">{syncError}</p>}
          <div className="ux-live-status">Ctrl+Enter / ⌘Enter 发送；普通 Enter 换行。<button className="button button-ghost" type="button" disabled={busy || applying} onClick={() => void openRevision(revision)}>刷新讨论状态</button></div>
          {revision.status === "applied" ? <footer className="book-revision-chat-complete"><Check size={16} /><span>{revision.proposal.replanFromChapter !== null ? "正典已应用，首批新章卡已触发生成。" : "本次修订已应用。"}需要继续调整时，请开始新的修订讨论。</span><button className="button button-secondary" type="button" onClick={reset}><RotateCcw size={15} />新建修订</button></footer> : <form className="book-revision-input" onSubmit={sendMessage}><textarea aria-label="整书修订消息" maxLength={20000} onKeyDown={(event) => { if ((event.ctrlKey || event.metaKey) && event.key === "Enter" && !event.nativeEvent.isComposing) { event.preventDefault(); event.currentTarget.form?.requestSubmit(); } }} value={message} onChange={(event) => setMessage(event.target.value)} placeholder={revision.status === "responding" ? "框架 Agent 正在整理当前草案" : "继续讨论、纠正草案，或补充必须保留的设定"} disabled={agentWorking || applying} /><button type="submit" className="icon-button" aria-label="发送整书修订消息" title={uncertain ? "请先刷新讨论状态" : "发送消息"} disabled={uncertain || agentWorking || applying || !message.trim()}><Send size={17} /></button></form>}
        </section>

        <aside className="book-revision-proposal">
          <header><div><span className="section-kicker">REVISION PROPOSAL</span><h2>修订草案</h2></div><span className={`revision-state revision-state-${revision.status}`}>{revisionStatusLabel(revision.status)}</span></header>
          {revision.error && <div className="book-revision-error"><AlertTriangle size={16} /><span>{revision.error}</span></div>}
          {hasProposal ? <>
            {revision.proposal.summary && <p className="book-revision-summary">{revision.proposal.summary}</p>}
            {projectPatchEntries.length > 0 && <section className="book-revision-metadata"><h3>项目信息</h3>{projectPatchEntries.map(([label, value]) => <div key={label}><span>{label}</span><strong>{value}</strong></div>)}</section>}
            {revision.proposal.changes.length > 0 && <section className="book-revision-documents"><header><h3>正典文档</h3><span>{revision.proposal.changes.length} 份</span></header>{revision.proposal.changes.map((change) => <details key={change.documentId}><summary><FileDiff size={15} /><span><strong>{change.name}</strong><small>{change.reason}</small></span><em>v{change.expectedVersion} → v{change.expectedVersion + 1}</em></summary><pre>{change.content}</pre></details>)}</section>}
            {revision.proposal.replanFromChapter !== null && <div className="book-revision-plan-impact"><ListTree size={17} /><div><strong>未来章纲需要复核</strong><span>从第 {revision.proposal.replanFromChapter} 章开始标记，已定稿正文保持不变。</span></div></div>}
            {revision.proposal.warnings.length > 0 && <section className="book-revision-warnings"><h3>应用前注意</h3>{revision.proposal.warnings.map((warning, index) => <p key={`${warning}-${index}`}><AlertTriangle size={13} />{warning}</p>)}</section>}
          </> : <div className="book-revision-proposal-empty"><MessageSquareText size={23} /><strong>继续确认修改方向</strong><span>讨论明确后将展示具体变动。</span></div>}
          <footer>
            {message.trim() && <p className="ux-note">还有未发送的补充意见，请先发送或清除输入，再应用修订。</p>}
            {revision.status === "applied" ? <div className="book-revision-applied"><Check size={16} /><span>{revision.appliedAt ? `${new Date(revision.appliedAt).toLocaleString("zh-CN")} 已写入` : "已写入"}{revision.proposal.replanFromChapter !== null ? " · 首批章卡已触发" : ""}</span></div>
              : revision.status === "ready" && hasProposal ? confirming ? <div className="book-revision-confirm"><p>确认写入当前草案？旧版将自动留档。</p><div><button className="button button-ghost" type="button" onClick={() => setConfirming(false)} disabled={applying}>再检查</button><button className="button button-primary" type="button" onClick={() => void applyRevision()} disabled={busy || applying}>{applying ? <span className="spinner" /> : <Check size={15} />}{applying ? "正在写入" : "确认应用"}</button></div></div> : <button className="button button-primary" type="button" onClick={() => setConfirming(true)} disabled={busy || applying || uncertain || Boolean(message.trim())}><Check size={15} />应用当前修订</button>
                : <p className="book-revision-not-ready">{revision.status === "responding" ? "正在整理修订草案..." : "继续讨论以形成完整草案。"}</p>}
          </footer>
        </aside>
      </main>}
    </div>,
    document.body
  );
}

function revisionScopeLabel(scope: BookRevisionScope): string {
  return ({ story: "整本故事", ledger: "设定账本", outline: "分卷章纲" })[scope];
}

function revisionStatusLabel(status: BookRevisionSession["status"]): string {
  return ({ discussing: "讨论中", responding: "正在整理", ready: "待确认", applied: "已应用" })[status];
}

function projectPatchRows(patch: BookRevisionProjectPatch): Array<[string, string]> {
  const rows: Array<[string, string]> = [];
  if (patch.title !== undefined) rows.push(["书名", patch.title]);
  if (patch.author !== undefined) rows.push(["作者", patch.author || "未署名"]);
  if (patch.summary !== undefined) rows.push(["项目简介", patch.summary]);
  if (patch.shelfBlurb !== undefined) rows.push(["宣传语", patch.shelfBlurb]);
  if (patch.tags !== undefined) rows.push(["标签", patch.tags.join("、")]);
  return rows;
}
